import React from "react";
import { ProgressBar } from "react-bootstrap";
import styled from "styled-components";
import { FormattedMessage, IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { DateRange, ExperimentResults, MetricRange, Verdict } from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { DecisionGates, resolveDecisionGates } from "../domain/decisionGates";
import { useExperimentResults } from "../application/hooks/useExperimentResults";
import { messages } from "../experiment.messages";

// range/custom keep US-017's lifted-state prop shape — `custom` is `DateRange | undefined`, not an
// optional prop, so the shell always passes its current value explicitly (undefined is a valid,
// expected value, not "prop omitted").
export interface VerdictBannerProps {
  propertyTicker: string;
  experimentId: string;
  range: MetricRange;
  custom: DateRange | undefined;
  repository: ExperimentRepository;
}

/**
 * The confidence at which `backoffice-manager` will declare a winner. Mirrored here only to choose
 * the wording of the reason sentence — the decision itself is the backend's
 * (`Verdict.NO_WINNER` ⇔ `confidence < 0.95`), never re-made here.
 */
const WINNER_CONFIDENCE_THRESHOLD = 0.95;

/** Names the panel's `role="region"` landmark. One decision panel per results page. */
const HEADLINE_ID = "decision-panel-headline";

/**
 * `backoffice-manager`'s own per-arm bookings minimum, used only when a response leaves
 * `min_bookings_threshold` at its `0` default — "Al menos 0 reservas por escenario" states a
 * condition that is not the one being applied.
 */
const DEFAULT_MIN_BOOKINGS_THRESHOLD = 50;

/**
 * The panel's three tones. There is deliberately no success green for "wait": a full green panel
 * while the test is still collecting is the single most misread thing on this screen.
 */
type Tone = "info" | "success" | "warning";

const TONES: Record<Tone, { stripe: string; pillBackground: string; pillText: string }> = {
  info: { stripe: "#17a2b8", pillBackground: "#e3f4f7", pillText: "#0f6674" },
  success: { stripe: "#28a745", pillBackground: "#e4f5e9", pillText: "#1c7430" },
  warning: { stripe: "#f0ad4e", pillBackground: "#fdf1e0", pillText: "#8a5a00" },
};

const GATE_MET_COLOR = TONES.success.stripe;
const GATE_PENDING_COLOR = TONES.warning.stripe;

/** A coloured left stripe rather than a coloured background: the panel stays readable, and the
 *  colour reads as a marker instead of as an alert the user has to dismiss. */
const Panel = styled.div<{ $tone: Tone }>`
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.buttonLightBorder};
  border-left: 4px solid ${({ $tone }) => TONES[$tone].stripe};
  border-radius: 0.25rem;
`;

const PanelBody = styled.div`
  display: flex;
  gap: 2rem;
  padding: 1rem 1.25rem;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const DecisionColumn = styled.div`
  flex: 1 1 55%;
  min-width: 0;
`;

const GatesColumn = styled.div`
  flex: 1 1 45%;
  min-width: 0;
`;

const Headline = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
`;

const StatePill = styled.span<{ $tone: Tone }>`
  display: inline-block;
  background: ${({ $tone }) => TONES[$tone].pillBackground};
  color: ${({ $tone }) => TONES[$tone].pillText};
  border-radius: 0.75rem;
  padding: 0.1rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const GateRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.35rem 0;
`;

const GateDot = styled.span<{ $met: boolean }>`
  flex: 0 0 auto;
  color: ${({ $met }) => ($met ? GATE_MET_COLOR : GATE_PENDING_COLOR)};
  font-weight: 700;
  line-height: 1.2;
`;

/** The split warning sits WITH the verdict, not instead of it: a highlighted row of its own,
 *  below the panel body, so the conclusion above stays readable and the caveat stays impossible
 *  to miss. */
const SrmWarningRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  border-top: 1px solid ${({ theme }) => theme.greyBorder};
  background: ${TONES.warning.pillBackground};
  color: ${TONES.warning.pillText};
  padding: 0.5rem 1.25rem;
  font-size: 0.8rem;
`;

const PanelFooter = styled.div`
  border-top: 1px dashed ${({ theme }) => theme.greyBorder};
  padding: 0.5rem 1.25rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.regularTextColor};
`;

/** `confidence`, `sampleProgress` and the shares arrive as fractions; the UI speaks percent. */
function toPercent(fraction: number): number {
  return fraction * 100;
}

interface DecisionState {
  tone: Tone;
  headline: React.ReactNode;
  pill: MessageDescriptor;
  testId: string;
}

function resolveDecisionState(data: ExperimentResults): DecisionState {
  // A broken split used to take over this headline entirely, which left the screen unable to
  // answer the question it exists for. It is a caveat ON the verdict, not a replacement for it,
  // so it is rendered as its own row below (see `SrmWarning`).
  switch (data.verdict) {
    case Verdict.VERDICT_WINNER_A:
    case Verdict.VERDICT_WINNER_B: {
      const side = data.verdict === Verdict.VERDICT_WINNER_A ? "A" : "B";
      const name = side === "A" ? data.scenarioA.variantTicker : data.scenarioB.variantTicker;
      return {
        tone: "success",
        headline: (
          <FormattedMessage {...messages.results.decision.headlineWinner} values={{ side, name }} />
        ),
        pill: messages.results.decision.pillWinner,
        testId: "decision-winner",
      };
    }
    case Verdict.VERDICT_NO_WINNER:
      return {
        tone: "warning",
        headline: <FormattedMessage {...messages.results.decision.headlineNoWinner} />,
        pill: messages.results.decision.pillNoWinner,
        testId: "decision-no-winner",
      };
    default:
      // VERDICT_COLLECTING, and defensively VERDICT_UNSPECIFIED: both mean "there is nothing to
      // decide yet", which is exactly what the collecting copy says.
      if (data.verdict === Verdict.VERDICT_UNSPECIFIED) {
        console.error("Unexpected VERDICT_UNSPECIFIED from GetExperimentResults", data);
      }
      return {
        tone: "info",
        headline: <FormattedMessage {...messages.results.decision.headlineCollecting} />,
        pill: messages.results.decision.pillCollecting,
        testId: "decision-collecting",
      };
  }
}

/**
 * ONE sentence of reason, built from the data. `lift` is always B over A, so a leading A is stated
 * with its sign removed and the two sides swapped, exactly as the winner copy has always done.
 */
function renderReason(data: ExperimentResults, intl: IntlShape): React.ReactNode {
  const oneDecimal = (value: number) =>
    intl.formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const confidence = intl.formatNumber(toPercent(data.confidence), { maximumFractionDigits: 0 });
  const format = (value: number) => intl.formatNumber(value, { maximumFractionDigits: 0 });

  const leader = data.lift >= 0 ? "B" : "A";
  const other = data.lift >= 0 ? "A" : "B";
  const absoluteLift = oneDecimal(Math.abs(data.lift));

  if (data.verdict === Verdict.VERDICT_WINNER_A || data.verdict === Verdict.VERDICT_WINNER_B) {
    const side = data.verdict === Verdict.VERDICT_WINNER_A ? "A" : "B";
    return (
      <FormattedMessage
        {...messages.results.decision.reasonWinner}
        values={{
          winner: side === "A" ? data.scenarioA.variantTicker : data.scenarioB.variantTicker,
          loser: side === "A" ? data.scenarioB.variantTicker : data.scenarioA.variantTicker,
          lift: absoluteLift,
          confidence,
        }}
      />
    );
  }

  if (data.verdict === Verdict.VERDICT_NO_WINNER) {
    return (
      <FormattedMessage
        {...messages.results.decision.reasonNoWinner}
        values={{ lift: absoluteLift, confidence }}
      />
    );
  }

  // COLLECTING: with no measurable gap there is nothing to attribute to either side yet.
  if (data.lift === 0) {
    return <FormattedMessage {...messages.results.decision.reasonCollectingFlat} />;
  }

  const descriptor =
    data.confidence >= WINNER_CONFIDENCE_THRESHOLD
      ? messages.results.decision.reasonCollecting
      : messages.results.decision.reasonCollectingLowConfidence;

  return (
    <FormattedMessage {...descriptor} values={{ leader, other, lift: absoluteLift, confidence }} />
  );
}

/**
 * The traffic split is not compatible with the configured one. Everything above it still stands
 * as the backend computed it — this says to check the split before acting on it.
 */
function SrmWarning({ data, intl }: { data: ExperimentResults; intl: IntlShape }) {
  const format = (value: number) => intl.formatNumber(value, { maximumFractionDigits: 0 });
  const observedA = toPercent(data.observedShareA);
  const expectedA = toPercent(data.expectedShareA);

  return (
    <SrmWarningRow data-testid="decision-srm">
      <span aria-hidden="true">⚠</span>
      <span>
        <strong>
          <FormattedMessage {...messages.results.decision.headlineSrm} />
        </strong>{" "}
        <FormattedMessage
          {...messages.results.decision.reasonSrm}
          values={{
            observedA: format(observedA),
            observedB: format(100 - observedA),
            expectedA: format(expectedA),
            expectedB: format(100 - expectedA),
          }}
        />
      </span>
    </SrmWarningRow>
  );
}

function Gate({
  met,
  label,
  value,
  testId,
  notApplicable = false,
  children,
}: React.PropsWithChildren<{
  met: boolean;
  label: React.ReactNode;
  value: React.ReactNode;
  testId: string;
  /** No condition was configured, so there is nothing to tick off — and a check mark next to
   *  "no minimum configured" reads as a condition that was actually cleared. */
  notApplicable?: boolean;
}>) {
  return (
    <div data-testid={testId}>
      <GateRow>
        {/* The dot's meaning is spelled out for a screen reader, so "met" is never colour-only. */}
        {!notApplicable && (
          <>
            <GateDot $met={met} aria-hidden="true">
              {met ? "✓" : "…"}
            </GateDot>
            <span className="sr-only">
              <FormattedMessage
                {...(met
                  ? messages.results.decision.gateMet
                  : messages.results.decision.gatePending)}
              />
            </span>
          </>
        )}
        <span className="flex-grow-1 small">{label}</span>
        <span className="small font-weight-bold text-nowrap">{value}</span>
      </GateRow>
      {children}
    </div>
  );
}

function SampleGateValue({
  data,
  gates,
  intl,
}: {
  data: ExperimentResults;
  gates: DecisionGates;
  intl: IntlShape;
}) {
  if (!gates.hasTarget) {
    // No usable target (the backend could not size the test, or sent its int64 sentinel): report
    // what has been collected rather than a percentage of a nonsense denominator.
    return (
      <FormattedMessage
        {...messages.results.decision.collectingNoTarget}
        values={{ collected: intl.formatNumber(gates.collected) }}
      />
    );
  }
  return (
    <FormattedMessage
      {...messages.results.decision.gateSampleValue}
      values={{
        collected: intl.formatNumber(gates.collected),
        required: intl.formatNumber(data.requiredVisitors),
        pct: intl.formatNumber(toPercent(data.sampleProgress), { maximumFractionDigits: 0 }),
      }}
    />
  );
}

/**
 * The single most important takeaway of the results screen: whether there is anything to decide
 * yet, and if not, exactly what is still missing. Every number below is a formatting transform of
 * a value `backoffice-manager` already computed — this component performs no statistics.
 *
 * Self-contained with respect to the results payload (US-017's architecture): it fetches its own
 * copy and relies on React Query's identical-query-key de-duplication to share the call with the
 * shell and its sibling sections.
 */
export function VerdictBanner({
  propertyTicker,
  experimentId,
  range,
  custom,
  repository,
}: VerdictBannerProps) {
  const intl = useIntl();
  const { data, isLoading, isError, error } = useExperimentResults(
    propertyTicker,
    experimentId,
    range,
    repository,
    custom
  );

  // Business Rule 7 (US-018): loading and error states render nothing, never a skeleton or a stale
  // panel — a wrong or momentarily-blank verdict is strictly worse than a brief absence.
  if (isLoading || isError || !data) {
    if (isError) {
      console.error("Error loading experiment results for VerdictBanner", error);
    }
    return null;
  }

  const state = resolveDecisionState(data);
  const gates = resolveDecisionGates(data);
  // Clamped because a bar cannot be more than full; the figure beside it is not, because
  // `sample_progress` is deliberately unclamped on the wire and "140 %" is real information.
  const barValue = Math.min(Math.max(toPercent(data.sampleProgress), 0), 100);
  // `minDays === 0` is "unset" on the wire, not "zero days required": there is no minimum to
  // state, and nothing for the gate to tick off.
  const hasMinDays = data.minDays > 0;

  return (
    // A landmark with no accessible name is just a landmark; the headline is what names it.
    <Panel
      $tone={state.tone}
      role="region"
      aria-labelledby={HEADLINE_ID}
      data-testid="decision-panel">
      <PanelBody>
        <DecisionColumn data-testid={state.testId}>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <Headline id={HEADLINE_ID}>{state.headline}</Headline>
            <StatePill $tone={state.tone} data-testid="decision-pill">
              <FormattedMessage {...state.pill} />
            </StatePill>
          </div>
          <p className="mb-0" data-testid="decision-reason">
            {renderReason(data, intl)}
          </p>
        </DecisionColumn>

        <GatesColumn>
          <div className="text-uppercase text-muted mb-1" style={{ fontSize: "0.7rem" }}>
            <FormattedMessage {...messages.results.decision.gatesTitle} />
          </div>

          <Gate
            testId="gate-sample"
            met={gates.sampleMet}
            label={<FormattedMessage {...messages.results.decision.gateSampleLabel} />}
            value={<SampleGateValue data={data} gates={gates} intl={intl} />}>
            {gates.hasTarget && (
              <ProgressBar
                now={barValue}
                style={{ height: "4px" }}
                className="mb-1"
                data-testid="sample-progress-bar"
              />
            )}
          </Gate>

          <Gate
            testId="gate-bookings"
            met={gates.bookingsMet}
            label={
              <FormattedMessage
                {...messages.results.decision.gateBookingsLabel}
                values={{
                  threshold: intl.formatNumber(
                    data.minBookingsThreshold > 0
                      ? data.minBookingsThreshold
                      : DEFAULT_MIN_BOOKINGS_THRESHOLD
                  ),
                }}
              />
            }
            value={
              <FormattedMessage
                {...messages.results.decision.gateBookingsValue}
                values={{
                  a: intl.formatNumber(data.scenarioA.bookings),
                  b: intl.formatNumber(data.scenarioB.bookings),
                }}
              />
            }
          />

          <Gate
            testId="gate-duration"
            met={gates.durationMet}
            notApplicable={!hasMinDays}
            label={
              hasMinDays ? (
                <FormattedMessage
                  {...messages.results.decision.gateDurationLabel}
                  values={{ minDays: data.minDays }}
                />
              ) : (
                <FormattedMessage {...messages.results.decision.gateDurationLabelNoMin} />
              )
            }
            value={
              hasMinDays ? (
                <FormattedMessage
                  {...messages.results.decision.gateDurationValue}
                  values={{ activeDays: data.activeDays, minDays: data.minDays }}
                />
              ) : (
                <FormattedMessage {...messages.results.decision.gateDurationValueNoMin} />
              )
            }
          />

          {/* Only shown when the backend says it has an honest estimate — never a fabricated one. */}
          {data.hasEstimatedDaysLeft && (
            <p className="text-muted small mt-1 mb-0" data-testid="decision-eta">
              <FormattedMessage
                {...messages.results.decision.etaDaysLeft}
                values={{ days: intl.formatNumber(data.estimatedDaysLeft) }}
              />
            </p>
          )}
        </GatesColumn>
      </PanelBody>

      {data.srmAlert && <SrmWarning data={data} intl={intl} />}

      {/* Moved out of the warnings banner: the cut-off is a standing property of the pipeline, not
          a degradation, and it belongs next to the figures it qualifies. */}
      <PanelFooter data-testid="decision-freshness-note">
        <FormattedMessage {...messages.results.decision.freshnessNote} />
      </PanelFooter>
    </Panel>
  );
}
